import { useState, useEffect } from 'react';
import { Mail, Send, Users, Trash2, Plus, Loader2, X, Eye, Edit, ChevronDown } from 'lucide-react';
import DOMPurify from 'dompurify';
import { formatDate, cn } from '../lib/utils';
import { toast } from 'sonner';

const API_URL = '';

const SEGMENTS = [
  { value: 'all', label: 'Tous (abonnes + clients)' },
  { value: 'newsletter', label: 'Abonnes newsletter uniquement' },
  { value: 'customers', label: 'Clients enregistres uniquement' },
];

const TEMPLATES = [
  {
    id: 'new_collection',
    name: 'Nouvelle Collection',
    subject: 'Decouvrez notre nouvelle collection !',
    html: `<p>Chere cliente,</p><p>Nous sommes ravies de vous presenter notre <strong>nouvelle collection</strong> !</p><p>Des pieces elegantes et raffinées, pensees pour sublimer chaque femme. Decouvrez les maintenant sur notre site.</p><p>A tres vite,<br/>L'equipe Bloom Lingerie</p>`,
  },
  {
    id: 'promo',
    name: 'Promotion Speciale',
    subject: 'Offre exclusive : -20% sur tout le site !',
    html: `<p>Chere cliente,</p><p>Profitez de <strong>-20% sur tout le site</strong> avec le code <strong style="color:#D4AF37;font-size:18px;">BLOOM20</strong></p><p>Offre limitee, ne la manquez pas !</p><p>A tres vite,<br/>L'equipe Bloom Lingerie</p>`,
  },
  {
    id: 'occasion',
    name: 'Occasion Speciale',
    subject: 'Un cadeau special pour vous !',
    html: `<p>Chere cliente,</p><p>Pour cette occasion speciale, nous avons prepare une selection unique pour vous.</p><p>Faites-vous plaisir ou offrez un cadeau inoubliable.</p><p>Avec amour,<br/>L'equipe Bloom Lingerie</p>`,
  },
  {
    id: 'custom',
    name: 'Email personnalise',
    subject: '',
    html: '',
  },
];

export default function AdminCampaigns() {
  const [campaigns, setCampaigns] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [subscribersTotal, setSubscribersTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('campaigns');
  const [showForm, setShowForm] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [sending, setSending] = useState(null);
  const [preview, setPreview] = useState(null);

  const [form, setForm] = useState({
    name: '',
    subject: '',
    html_content: '',
    segment: 'all',
    template: 'custom',
  });

  const fetchCampaigns = async () => {
    try {
      const res = await fetch(`${API_URL}/api/bloom-gestion-secure/campaigns`, { credentials: 'include' });
      const data = await res.json();
      setCampaigns(data.campaigns || []);
    } catch { /* ignore */ }
  };

  const fetchSubscribers = async () => {
    try {
      const res = await fetch(`${API_URL}/api/bloom-gestion-secure/subscribers`, { credentials: 'include' });
      const data = await res.json();
      setSubscribers(data.subscribers || []);
      setSubscribersTotal(data.total || 0);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    Promise.all([fetchCampaigns(), fetchSubscribers()]).finally(() => setLoading(false));
  }, []);

  const handleTemplateChange = (templateId) => {
    const tpl = TEMPLATES.find(t => t.id === templateId);
    if (tpl) {
      setForm(prev => ({
        ...prev,
        template: templateId,
        subject: tpl.subject || prev.subject,
        html_content: tpl.html || prev.html_content,
        name: tpl.name !== 'Email personnalise' ? tpl.name : prev.name,
      }));
    }
  };

  const resetForm = () => {
    setForm({ name: '', subject: '', html_content: '', segment: 'all', template: 'custom' });
    setEditingCampaign(null);
    setShowForm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.subject || !form.html_content) {
      toast.error('Remplissez tous les champs');
      return;
    }

    const payload = { name: form.name, subject: form.subject, html_content: form.html_content, segment: form.segment };

    try {
      const url = editingCampaign
        ? `${API_URL}/api/bloom-gestion-secure/campaigns/${editingCampaign.campaign_id}`
        : `${API_URL}/api/bloom-gestion-secure/campaigns`;
      const method = editingCampaign ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });

      if (res.ok) {
        toast.success(editingCampaign ? 'Campagne mise a jour' : 'Campagne creee');
        resetForm();
        fetchCampaigns();
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Erreur');
      }
    } catch {
      toast.error('Erreur de connexion');
    }
  };

  const handleSend = async (campaign) => {
    if (!window.confirm(`Envoyer "${campaign.name}" a ${campaign.segment === 'all' ? 'tous les contacts' : campaign.segment === 'newsletter' ? 'abonnes newsletter' : 'clients'} ?`)) return;
    setSending(campaign.campaign_id);
    try {
      const res = await fetch(`${API_URL}/api/bloom-gestion-secure/campaigns/${campaign.campaign_id}/send`, {
        method: 'POST',
        credentials: 'include',
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(data.message || 'Envoi lance');
        fetchCampaigns();
      } else {
        toast.error(data.detail || 'Erreur');
      }
    } catch {
      toast.error('Erreur de connexion');
    } finally {
      setSending(null);
    }
  };

  const handleDelete = async (campaign) => {
    if (!window.confirm(`Supprimer la campagne "${campaign.name}" ?`)) return;
    try {
      const res = await fetch(`${API_URL}/api/bloom-gestion-secure/campaigns/${campaign.campaign_id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (res.ok) {
        toast.success('Campagne supprimee');
        fetchCampaigns();
      }
    } catch { /* ignore */ }
  };

  const handleDeleteSubscriber = async (sub) => {
    if (!window.confirm(`Desabonner ${sub.email} ?`)) return;
    try {
      const res = await fetch(`${API_URL}/api/bloom-gestion-secure/subscribers/${sub.subscriber_id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (res.ok) {
        toast.success('Abonne supprime');
        fetchSubscribers();
      }
    } catch { /* ignore */ }
  };

  const handleEdit = (campaign) => {
    setForm({
      name: campaign.name,
      subject: campaign.subject,
      html_content: campaign.html_content,
      segment: campaign.segment,
      template: 'custom',
    });
    setEditingCampaign(campaign);
    setShowForm(true);
  };

  const statusBadge = (status) => {
    const map = {
      draft: 'bg-gray-100 text-gray-600',
      sending: 'bg-yellow-100 text-yellow-700',
      sent: 'bg-green-100 text-green-700',
      failed: 'bg-red-100 text-red-700',
    };
    const labels = { draft: 'Brouillon', sending: 'Envoi...', sent: 'Envoye', failed: 'Echec' };
    return (
      <span className={cn('px-2 py-0.5 text-xs font-medium rounded-full', map[status] || 'bg-gray-100')}>
        {labels[status] || status}
      </span>
    );
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="animate-spin" size={24} /></div>;

  return (
    <div data-testid="admin-campaigns-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-serif font-bold" data-testid="campaigns-title">Email & Newsletters</h1>
          <p className="text-sm text-[#6B6A68] mt-1">{subscribersTotal} abonne{subscribersTotal > 1 ? 's' : ''}</p>
        </div>
        <button
          onClick={() => { resetForm(); setShowForm(true); }}
          className="flex items-center gap-2 bg-[#1A1514] text-white px-5 py-2.5 text-sm font-medium hover:bg-[#2a2524] transition-colors"
          data-testid="new-campaign-btn"
        >
          <Plus size={16} /> Nouvelle campagne
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-[#E0DFDD] mb-6">
        <button
          onClick={() => setTab('campaigns')}
          className={cn('px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors', tab === 'campaigns' ? 'border-[#1A1514] text-[#1A1514]' : 'border-transparent text-[#6B6A68] hover:text-[#1A1514]')}
          data-testid="tab-campaigns"
        >
          <Mail size={14} className="inline mr-1.5" /> Campagnes ({campaigns.length})
        </button>
        <button
          onClick={() => setTab('subscribers')}
          className={cn('px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors', tab === 'subscribers' ? 'border-[#1A1514] text-[#1A1514]' : 'border-transparent text-[#6B6A68] hover:text-[#1A1514]')}
          data-testid="tab-subscribers"
        >
          <Users size={14} className="inline mr-1.5" /> Abonnes ({subscribersTotal})
        </button>
      </div>

      {/* Campaigns Tab */}
      {tab === 'campaigns' && (
        <div className="space-y-3">
          {campaigns.length === 0 ? (
            <div className="text-center py-16 text-[#6B6A68]">
              <Mail size={40} className="mx-auto mb-4 opacity-30" />
              <p>Aucune campagne. Creez votre premiere newsletter !</p>
            </div>
          ) : (
            campaigns.map(c => (
              <div key={c.campaign_id} className="bg-white border border-[#E0DFDD] p-4 flex items-center justify-between" data-testid={`campaign-${c.campaign_id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-medium text-sm truncate">{c.name}</h3>
                    {statusBadge(c.status)}
                  </div>
                  <p className="text-xs text-[#6B6A68] mt-1 truncate">{c.subject}</p>
                  <p className="text-xs text-[#9B9A98] mt-0.5">
                    {c.sent_at ? `Envoye le ${formatDate(c.sent_at)}` : `Cree le ${formatDate(c.created_at)}`}
                    {c.sent_count > 0 && ` · ${c.sent_count} envoyes`}
                    {c.failed_count > 0 && ` · ${c.failed_count} echecs`}
                  </p>
                </div>
                <div className="flex items-center gap-2 ml-4">
                  <button onClick={() => setPreview(c)} className="p-2 text-[#6B6A68] hover:text-[#1A1514]" title="Apercu" data-testid={`preview-${c.campaign_id}`}>
                    <Eye size={16} />
                  </button>
                  {c.status === 'draft' && (
                    <>
                      <button onClick={() => handleEdit(c)} className="p-2 text-[#6B6A68] hover:text-[#1A1514]" title="Modifier" data-testid={`edit-${c.campaign_id}`}>
                        <Edit size={16} />
                      </button>
                      <button
                        onClick={() => handleSend(c)}
                        disabled={sending === c.campaign_id}
                        className="flex items-center gap-1.5 bg-[#D4AF37] text-white px-3 py-1.5 text-xs font-medium hover:bg-[#c4a030] disabled:opacity-50"
                        data-testid={`send-${c.campaign_id}`}
                      >
                        {sending === c.campaign_id ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
                        Envoyer
                      </button>
                    </>
                  )}
                  <button onClick={() => handleDelete(c)} className="p-2 text-[#6B6A68] hover:text-red-500" title="Supprimer" data-testid={`delete-${c.campaign_id}`}>
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Subscribers Tab */}
      {tab === 'subscribers' && (
        <div className="space-y-1">
          {subscribers.length === 0 ? (
            <div className="text-center py-16 text-[#6B6A68]">
              <Users size={40} className="mx-auto mb-4 opacity-30" />
              <p>Aucun abonne pour le moment.</p>
            </div>
          ) : (
            <div className="bg-white border border-[#E0DFDD]">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[#E0DFDD] bg-[#F9F8F6]">
                    <th className="text-left p-3 font-medium">Email</th>
                    <th className="text-left p-3 font-medium">Date</th>
                    <th className="text-right p-3 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {subscribers.map(sub => (
                    <tr key={sub.subscriber_id} className="border-b border-[#E0DFDD] last:border-0" data-testid={`sub-${sub.subscriber_id}`}>
                      <td className="p-3">{sub.email}</td>
                      <td className="p-3 text-[#6B6A68]">{formatDate(sub.created_at)}</td>
                      <td className="p-3 text-right">
                        <button onClick={() => handleDeleteSubscriber(sub)} className="text-[#6B6A68] hover:text-red-500" data-testid={`unsub-${sub.subscriber_id}`}>
                          <Trash2 size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Campaign Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white max-w-2xl w-full max-h-[90vh] overflow-y-auto" data-testid="campaign-form-modal">
            <div className="sticky top-0 bg-[#1A1514] text-white p-4 flex items-center justify-between z-10">
              <h2 className="font-serif text-lg">{editingCampaign ? 'Modifier la campagne' : 'Nouvelle campagne'}</h2>
              <button onClick={resetForm} className="p-1 hover:bg-white/10 rounded" data-testid="close-campaign-form">
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-5">
              {/* Template selector */}
              {!editingCampaign && (
                <div>
                  <label className="text-sm font-medium block mb-2">Modele</label>
                  <div className="grid grid-cols-2 gap-2">
                    {TEMPLATES.map(tpl => (
                      <button
                        key={tpl.id}
                        type="button"
                        onClick={() => handleTemplateChange(tpl.id)}
                        className={cn(
                          'p-3 border text-left text-sm transition-colors',
                          form.template === tpl.id ? 'border-[#1A1514] bg-[#F9F8F6]' : 'border-[#E0DFDD] hover:border-[#1A1514]'
                        )}
                        data-testid={`tpl-${tpl.id}`}
                      >
                        {tpl.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <label className="text-sm font-medium block mb-2">Nom de la campagne</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={e => setForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Ex: Newsletter Mars 2026"
                  className="w-full border border-[#E0DFDD] p-3 text-sm focus:outline-none focus:border-[#1A1514]"
                  data-testid="input-campaign-name"
                />
              </div>

              <div>
                <label className="text-sm font-medium block mb-2">Objet de l'email</label>
                <input
                  type="text"
                  value={form.subject}
                  onChange={e => setForm(prev => ({ ...prev, subject: e.target.value }))}
                  placeholder="Ex: Decouvrez notre nouvelle collection !"
                  className="w-full border border-[#E0DFDD] p-3 text-sm focus:outline-none focus:border-[#1A1514]"
                  data-testid="input-campaign-subject"
                />
              </div>

              <div>
                <label className="text-sm font-medium block mb-2">Segment</label>
                <select
                  value={form.segment}
                  onChange={e => setForm(prev => ({ ...prev, segment: e.target.value }))}
                  className="w-full border border-[#E0DFDD] p-3 text-sm focus:outline-none focus:border-[#1A1514] bg-white"
                  data-testid="select-segment"
                >
                  {SEGMENTS.map(s => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-sm font-medium block mb-2">Contenu HTML</label>
                <textarea
                  value={form.html_content}
                  onChange={e => setForm(prev => ({ ...prev, html_content: e.target.value }))}
                  rows={10}
                  placeholder="<p>Votre message ici...</p>"
                  className="w-full border border-[#E0DFDD] p-3 text-sm focus:outline-none focus:border-[#1A1514] font-mono resize-y"
                  data-testid="input-campaign-content"
                />
              </div>

              <div className="flex gap-3">
                <button type="button" onClick={resetForm} className="flex-1 border border-[#E0DFDD] py-3 text-sm font-medium">
                  Annuler
                </button>
                <button
                  type="submit"
                  className="flex-1 bg-[#1A1514] text-white py-3 text-sm font-medium"
                  data-testid="save-campaign-btn"
                >
                  {editingCampaign ? 'Mettre a jour' : 'Creer la campagne'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {preview && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white max-w-2xl w-full max-h-[90vh] overflow-y-auto" data-testid="campaign-preview-modal">
            <div className="sticky top-0 bg-[#1A1514] text-white p-4 flex items-center justify-between z-10">
              <h2 className="font-serif text-lg">Apercu : {preview.name}</h2>
              <button onClick={() => setPreview(null)} className="p-1 hover:bg-white/10 rounded" data-testid="close-preview">
                <X size={20} />
              </button>
            </div>
            <div className="p-4">
              <div className="bg-[#F9F8F6] p-3 mb-4 text-sm">
                <p><strong>Objet :</strong> {preview.subject}</p>
                <p><strong>Segment :</strong> {SEGMENTS.find(s => s.value === preview.segment)?.label}</p>
              </div>
              <div className="border border-[#E0DFDD] p-6 text-sm" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(preview.html_content || '', { USE_PROFILES: { html: true } }) }} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
